import React, { useState } from 'react';
import { Calculator, Plus } from 'lucide-react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';

// Atomic Sub-Modules
import { MetabolicAIInput } from './nutrition/MetabolicAIInput';
import { MetabolicLedgerRow } from './nutrition/MetabolicLedgerRow';
import { MetabolicSummary } from './nutrition/MetabolicSummary';
import { BioLoadPreview } from '../shared/BioLoadPreview';

// Headless Hook
import { useNutritionComposer } from '../../hooks/useNutritionComposer';

interface NutritionBodyProps {
    data: any;
    updatePayload: (updates: any) => void;
    updatePreviewData: (data: any) => void;
}

/**
 * NutritionBody: Standardized Metabolic Ledger (v4).
 * V4.5: Enhanced with Bio-Load Preview for compound analysis.
 */
export const NutritionBody: React.FC<NutritionBodyProps> = ({ data, updatePayload, updatePreviewData }) => {
    const { payload = {} } = data;
    
    const {
        items,
        totals,
        tacticalTip,
        isAnalyzing,
        rawDescription,
        setRawDescription,
        handleAIAnalyze,
        handleAddItem,
        handleRemove,
        handleUpdate,
        handleUpdateItem,
        updateSequenceStrategy
    } = useNutritionComposer({ payload, updatePayload, updatePreviewData });

    const [isBioLoadExpanded, setIsBioLoadExpanded] = useState(false);

    return (
        <Box className="animate-in fade-in slide-in-from-top-2" style={{ position: 'relative', minHeight: 400 }}>
            <style>{`
                .metabolic-input::-webkit-outer-spin-button,
                .metabolic-input::-webkit-inner-spin-button { -webkit-appearance: none !important; margin: 0 !important; }
                .metabolic-input { -moz-appearance: textfield !important; }
            `}</style>
            
            <MetabolicAIInput 
                value={rawDescription}
                onChange={setRawDescription}
                onAnalyze={handleAIAnalyze}
                isAnalyzing={isAnalyzing}
            />

            <Box style={{ padding: '5px 0' }}>
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, padding: '0 10px' }}>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Calculator size={14} color="#666" />
                        <Typography style={{ fontSize: 10, fontWeight: '700', color: '#888', letterSpacing: 2 }}>METABOLIC LEDGER</Typography>
                    </Box>
                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        <Pressable 
                            onPress={handleAddItem}
                            style={{ 
                                backgroundColor: 'rgba(242, 155, 17, 0.1)', 
                                padding: '4px 8px', 
                                borderRadius: 6, 
                                display: 'flex', 
                                flexDirection: 'row', 
                                alignItems: 'center', 
                                gap: 4,
                                border: '1px solid rgba(242, 155, 17, 0.2)'
                            }}
                        >
                            <Plus size={10} color="#f29b11" strokeWidth={3} />
                            <Typography style={{ fontSize: 8, fontWeight: '900', color: '#f29b11' }}>ADD ITEM</Typography>
                        </Pressable>
                        <Typography style={{ fontSize: 9, fontWeight: '500', color: '#444' }}>{items.length} ITEMS</Typography>
                    </Box>
                </Box>

                {items.length === 0 ? (
                    <Typography style={{ padding: '30px 0', textAlign: 'center', color: '#333', fontSize: 11, fontWeight: '500' }}>LEDGER EMPTY</Typography>
                ) : (
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {items.map((it: any, index: number) => (
                            <MetabolicLedgerRow 
                                key={`${it.id || index}-${index}`}
                                item={it}
                                onUpdate={handleUpdate}
                                onUpdateItem={handleUpdateItem}
                                onRemove={handleRemove}
                            />
                        ))}

                        {/* 2.5 Bio-Load Mapping (New Feature) */}
                        <BioLoadPreview 
                            data={totals} 
                            isExpanded={isBioLoadExpanded} 
                            setIsExpanded={setIsBioLoadExpanded} 
                            title="MEAL BIO-LOAD ANALYSIS"
                            accentColor="#2ecc71"
                        />

                        {/* 2.6 Meal Sequencing Strategy */}
                        <Box style={{ marginTop: 15, marginBottom: 5, padding: 15, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)' }}>
                            <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                <Typography style={{ fontSize: 10, fontWeight: '900', color: '#888', letterSpacing: 1.5, textTransform: 'uppercase' }}>Meal Sequencing</Typography>
                                <Typography style={{ fontSize: 8, fontWeight: '900', color: (payload.sequence_strategy === 'OPTIMIZED') ? '#2ecc71' : (payload.sequence_strategy === 'SPIKE' ? '#ef4444' : '#f29b11'), letterSpacing: 1 }}>
                                    {(payload.sequence_strategy || 'MIXED') === 'OPTIMIZED' ? 'PROTECTIVE' : (payload.sequence_strategy === 'SPIKE' ? 'VOLATILE' : 'BALANCED')}
                                </Typography>
                            </Box>
                            
                            <Box style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                                {[
                                    { id: 'SPIKE', label: 'CARBS FIRST', color: '#ef4444', desc: 'Fast Spike' },
                                    { id: 'MIXED', label: 'MIXED', color: '#f29b11', desc: 'Standard' },
                                    { id: 'OPTIMIZED', label: 'BUFFERED', color: '#2ecc71', desc: 'P+F+F First' }
                                ].map(strat => {
                                    const isActive = (payload.sequence_strategy || 'MIXED') === strat.id;
                                    return (
                                        <Pressable 
                                            key={strat.id}
                                            onPress={() => updateSequenceStrategy(strat.id)}
                                            style={{ 
                                                flex: 1, 
                                                paddingVertical: 12, 
                                                borderRadius: 10, 
                                                borderWidth: 1, 
                                                borderColor: isActive ? strat.color : '#222',
                                                backgroundColor: isActive ? strat.color + "15" : 'transparent',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography style={{ fontSize: 8, fontWeight: '900', color: isActive ? strat.color : '#444', marginBottom: 2 }}>{strat.label}</Typography>
                                            <Typography style={{ fontSize: 7, fontWeight: '700', color: isActive ? strat.color + "80" : '#333', textTransform: 'uppercase' }}>{strat.desc}</Typography>
                                        </Pressable>
                                    );
                                })}
                            </Box>
                        </Box>

                        {tacticalTip && (
                            <Box style={{ 
                                backgroundColor: 'rgba(242, 155, 17, 0.05)', 
                                padding: 12, 
                                borderRadius: 10, 
                                borderLeftWidth: 4, 
                                borderLeftColor: '#f29b11', 
                                width: '100%', 
                                marginTop: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                flexShrink: 1
                            }}>
                                <Typography style={{ fontSize: 10, color: '#f29b11', fontWeight: '900', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>💡 Tactical Adjustment</Typography>
                                <Typography style={{ fontSize: 12, color: '#eee', lineHeight: 18, textAlign: 'left', flexWrap: 'wrap', width: '100%' } as any}>
                                    {tacticalTip}
                                </Typography>
                            </Box>
                        )}

                        <MetabolicSummary totals={totals} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
